import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid'
import { Button } from '../ui/button'
import { Separator } from '../ui/separator'
import { Label } from '../ui/Label'
import { Input } from '../ui/Input'
import Field from '../ui/Field'
import SimpleComboBox from '../SimpleComboBox'
import { Description } from '../ui/Description'
import FieldStatusMessage from '../ui/FieldStatusMessage'
import { RadioGroup, RadioGroupItem } from '../ui/RadioGroup'
import { useController, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import _ from 'lodash'
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group'
import DatePicker from '../ui/DatePicker'
import * as yup from 'yup'
import { formatDate, getDate, getMonth } from 'date-fns'
import useCreateCustomTransactionRepeat from '@/services/api/cash-flow/transaction/useCreateCustomTransactionRepeat'
import useGetTransactionRepeatOptions from '@/services/api/cash-flow/transaction/useGetTransactionRepeatOptions'
import { getParts, buildCron, buildList } from '@/utils/cron.js'

const frequencyTypeOptions = [
  { id: 'Daily', name: 'Day' },
  { id: 'Weekly', name: 'Week' },
  { id: 'Monthly', name: 'Month' },
  { id: 'Annually', name: 'Year' },
] as const



export default function CustomTransactionRepeat({
  open,
  onOpenChange,
  transactionDate,
  series = undefined,
  submit
}) {


  const yupSchema = yup
    .object()
    .noUnknown(true) // validate keys
    .shape({
      frequencyType: yup.string().nullable().required(),
      frequency: yup.number().positive().min(1).required(),
      stopType: yup.string().default('never').required(),
    })
    .when((value, schema) => {
      if (value[0].stopType === 'date') {
        return schema.shape({
          stopDate: yup.date().required(),
        })
      }

      if (value[0].stopType === 'endsAfterTimes') {
        return schema.shape({
          endsAfterTimes: yup.number().positive().min(1).required(),
        })
      }
    })
    .when((value, schema) => {
      if (value[0].frequencyType === 'Weekly') {
        return schema.shape({
          weekDays: yup.array().required().min(1),
        })
      }

      if (value[0].frequencyType === 'Monthly') {
        return schema
          .shape({
            monthlyOption: yup.object().required().label('Monthly Option'),
          })
          // .when((data, schemaMonthly) => {
          //   if (data[0].monthlyOption === 'date') {
          //     return schemaMonthly.shape({
          //       monthlyDate: yup.number().positive().min(1).max(31),
          //     })
          //   }
          //   if (data[0].monthlyOption === 'dayOfTheMonth') {
          //     return schemaMonthly.shape({
          //       monthlyWeekDay: yup.string().required(),
          //       monthlyWeekNumber: yup
          //         .object({ id: yup.number(), name: yup.string() })
          //         .required(),
          //     })
          //   }
          //   return schemaMonthly
          // })
      }

      return schema
    })

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    register,
  } = useForm({
    // resolver: zodResolver(schema),
    resolver: yupResolver(yupSchema),
    defaultValues: async () => {
      const type = series?.type ?? 'Weekly'
      const frequency = series?.frequency ?? 1
      const cron = series?.cron ?? formatDate(new Date(transactionDate), '0 0 * * iii')
      setCronExpression(cron)
      const parts = getParts(cron)

      const shortcuts = buildList(transactionDate)

      return {
        frequencyType: type,
        frequency: frequency ?? 1,
        weekDays:
            type === 'Weekly'
                ? (parts.weekDay.split(',')).map((x) =>
                    _.startCase(x.toLowerCase()),
                )
                : [],
        monthlyDate: parts.day
            ? parts.day
            : '1',
        monthlyOption: parts.day != '*'
            ? shortcuts[4]
            : shortcuts[5],
        stopType: series?.ends_after_date
            ? 'date'
            : series?.ends_after_count
                ? 'endsAfterTimes'
                : 'never',
        stopDate: series?.ends_after_date
            ? new Date(series?.ends_after_date)
            : new Date(),
        endsAfterTimes: series?.ends_after_count ?? 1,
      }
    },
  })

  const { field: stopTypeField } = useController({
    control,
    name: 'stopType',
  })

  const { field: frequencyTypeField } = useController({
    control,
    name: 'frequencyType',
  })

  const { field: weekDaysField } = useController({
    control,
    name: 'weekDays',
    shouldUnregister: true,
  })

  const { field: monthlyOptionField } = useController({
    control,
    name: 'monthlyOption',
  })

  const { field: stopDateField } = useController({
    control,
    name: 'stopDate',
  })

  const [ cronExpression, setCronExpression ] = useState(series?.cron)

  const frequency = useWatch({
    control,
    name: 'frequency',
  })

  const repeatType = useWatch({
    control,
    name: 'frequencyType',
  })

  const monthlyOption = useWatch({
    control,
    name: 'monthlyOption',
  })

  const endsAfterTimes = useWatch({
    control,
    name: 'endsAfterTimes',
  })

  const monthlyDate = useWatch({
    control,
    name: 'monthlyDate',
  })

  const monthlyOptions = () => {

    const list = buildList(transactionDate)

    return [
        list[4],
        list[5]
    ]
  }

  function updateCoundBy(val, field) {
    let currentValue = getValues(field)
    let updatedValue =
      Number.isNaN(currentValue) || currentValue.toString() === ''
        ? 1
        : parseFloat(currentValue.toString()) + val

    updatedValue = updatedValue < 1 ? 1 : (updatedValue > 31 ? 31 : updatedValue)

    setValue(field, updatedValue.toString(), { shouldValidate: true })
  }

   useEffect(() => {
     setCronExpression(buildCron(frequencyTypeField.value,
         frequency,
         monthlyOptionField.value,
         formatDate(new Date(transactionDate), 'M'),
         monthlyOptionField.value?.cron,
         weekDaysField?.value ?? [],
         monthlyDate))
   }, [frequency, frequencyTypeField]);

  async function onSubmit(val) {
    if (!cronExpression)
      return

    const endsAfterDate = val?.stopType === 'date' ? val?.stopDate : undefined
    const endsAfterCount = val?.stopType === 'endsAfterTimes' ? val?.endsAfterTimes : undefined

    submit(val?.frequencyType, val?.frequency, cronExpression, endsAfterCount, endsAfterDate)

    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onSubmit={handleSubmit(onSubmit)}
        className="h-[37.5rem] max-h-[37.5rem]"
        as="form"
      >
        <DialogHeader>
          <DialogTitle>Custom Transaction Series</DialogTitle>
          <DialogClose>
            <XMarkIcon />
          </DialogClose>
        </DialogHeader>
        <Separator />
        <DialogBody className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <div className="rounded-md border border-light">
              <Label className="bg-gray-low px-3 py-2 text-txt-secondary">
                Period
              </Label>
              <div className="p-3">
                <div className="flex flex-col gap-field-gap">
                  <Field
                    status={errors.frequency?.message ? 'error' : undefined}
                    statusText={errors.frequency?.message}
                  >
                    <Label className="">Repeat every</Label>
                    <div data-slot="control" className="!mt-3 flex gap-4">
                      <div className="flex gap-2">
                        <div className="w-12 ">
                          <Input
                            type="number"
                            className="text-right"
                            {...register('frequency')}
                          />
                        </div>
                        <div className="flex flex-col justify-between gap-1">
                          <Button
                            type="button"
                            iconOnly
                            size="sm"
                            variant="ghost"
                            className="size-4 rounded-full"
                            onClick={() => updateCoundBy(1, 'frequency')}
                          >
                            <ChevronUpIcon />
                          </Button>
                          <Button
                            disabled={parseInt(frequency) <= 1}
                            type="button"
                            iconOnly
                            size="sm"
                            variant="ghost"
                            className="size-4 rounded-full"
                            onClick={() => updateCoundBy(-1, 'frequency')}
                          >
                            <ChevronDownIcon />
                          </Button>
                        </div>
                      </div>
                      <div className="w-32">
                        <Field>
                          <SimpleComboBox
                            items={frequencyTypeOptions.map((x) => x.id)}
                            selected={frequencyTypeField.value}
                            setSelected={(e) => {
                              frequencyTypeField.onChange(e)
                            }}
                            getOptionLabel={(item) => (
                              <div>
                                <p>
                                  {
                                    frequencyTypeOptions.find(
                                      (x) => x.id === item,
                                    )?.name
                                  }
                                  {parseInt(frequency) > 1 ? 's' : null}
                                </p>
                              </div>
                            )}
                            getSelectedLabel={(item) =>
                              `${
                                frequencyTypeOptions.find((x) => x.id === item)
                                  ?.name
                              }${parseInt(frequency) > 1 ? 's' : ''}`
                            }
                            getValue={(item) => item}
                            searchable={false}
                          />
                        </Field>
                      </div>
                    </div>

                    <FieldStatusMessage />
                  </Field>
                  <FieldStatusMessage
                    status={errors.frequencyType?.message ? 'error' : undefined}
                    statusText={`FrequencyType: ${errors.frequencyType?.message?.toString()}`}
                  />

                  {repeatType === 'Monthly' && (
                      <div>
                        <Field>
                          <SimpleComboBox
                              items={monthlyOptions()}
                              selected={monthlyOptionField.value}
                              setSelected={
                                monthlyOptionField.onChange
                              }
                              getOptionLabel={(item) => item?.short_title}
                              getSelectedLabel={(item) => item?.short_title}
                              getValue={(item) => item}
                              searchable={false}
                          />
                          <FieldStatusMessage
                              isolated
                              status={
                                errors.monthlyOptionField?.message
                                    ? 'error'
                                    : undefined
                              }
                              statusText={
                                errors.monthlyOptionField
                                    ?.message as string
                              }
                          />
                        </Field>
                    </div>
                  )}

                  { repeatType === 'Weekly' && (
                    <div>
                      <Field className="">
                        <Label>On</Label>
                        <div
                          data-slot="control"
                          className="flex flex-col gap-field-gap"
                        >
                          {/* Day */}
                          {repeatType === 'Weekly' && (
                            <div>
                              <ToggleGroup
                                size="sm"
                                type="multiple"
                                value={weekDaysField.value}
                                onValueChange={(val) => {
                                  weekDaysField.onChange(val)
                                }}
                              >
                                <ToggleGroupItem value="Mon">
                                  Mon
                                </ToggleGroupItem>
                                <ToggleGroupItem value="Tue">
                                  Tue
                                </ToggleGroupItem>
                                <ToggleGroupItem value="Wed">
                                  Wed
                                </ToggleGroupItem>
                                <ToggleGroupItem value="Thu">
                                  Thu
                                </ToggleGroupItem>
                                <ToggleGroupItem value="Fri">
                                  Fri
                                </ToggleGroupItem>
                                <ToggleGroupItem value="Sat">
                                  Sat
                                </ToggleGroupItem>
                                <ToggleGroupItem value="Sun">
                                  Sun
                                </ToggleGroupItem>
                              </ToggleGroup>

                              <FieldStatusMessage
                                isolated
                                status={
                                  errors.weekDays?.message ? 'error' : undefined
                                }
                                statusText={errors.weekDays?.message}
                              />
                            </div>
                          )}
                       </div>
                      </Field>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-md border border-light">
              <Label className="bg-gray-low px-3 py-2 text-txt-secondary">
                Stop Period
              </Label>

              <div className="p-3">
                <RadioGroup
                  value={stopTypeField.value}
                  onValueChange={stopTypeField.onChange}
                >
                  <Field>
                    <RadioGroupItem value="never" />
                    <Label>Never</Label>
                  </Field>
                  <div className="flex flex-col gap-field-gap">
                    <Field>
                      <RadioGroupItem value="date" />
                      <Label>Date</Label>
                    </Field>
                    {stopTypeField.value === 'date' && (
                      <DatePicker
                        value={stopDateField.value}
                        selected={new Date(stopDateField.value)}
                        onSelect={(val) =>
                          stopDateField.onChange(new Date(val))
                        }
                        disabled={{ before: new Date() }}
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-field-gap">
                    <Field>
                      <RadioGroupItem value="endsAfterTimes" />
                      <Label>After Repetitions</Label>
                    </Field>
                    {stopTypeField.value === 'endsAfterTimes' && (
                      <div>
                        <div className="flex gap-2">
                          <div className="w-12 ">
                            <Input
                              type="number"
                              className="text-right"
                              {...register('endsAfterTimes')}
                            />
                          </div>
                          <div className="flex flex-col justify-between gap-1">
                            <Button
                              type="button"
                              iconOnly
                              size="sm"
                              variant="ghost"
                              className="size-4 rounded-full"
                              onClick={() => updateCoundBy(1, 'endsAfterTimes')}
                            >
                              <ChevronUpIcon />
                            </Button>
                            <Button
                              disabled={parseInt(endsAfterTimes) <= 1}
                              type="button"
                              iconOnly
                              size="sm"
                              variant="ghost"
                              className="size-4 rounded-full"
                              onClick={() =>
                                updateCoundBy(-1, 'endsAfterTimes')
                              }
                            >
                              <ChevronDownIcon />
                            </Button>
                          </div>
                        </div>
                        <FieldStatusMessage
                          isolated
                          status={
                            errors.endsAfterTimes?.message ? 'error' : undefined
                          }
                          statusText={errors.endsAfterTimes?.message.toString()}
                        />
                      </div>
                    )}
                  </div>
                </RadioGroup>
                <FieldStatusMessage
                  isolated
                  status={errors.stopType?.message ? 'error' : undefined}
                  statusText={errors.stopType?.message.toString()}
                />
              </div>
            </div>
          </div>
        </DialogBody>
        <Separator />
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </DialogClose>

          <Button loading={status.isPending}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
